import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { ArrowUpRight } from "lucide-react";
import axios from "axios";
import { BilingualEyebrow, BilingualHeading } from "@/components/ui/bilingual-heading";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fallbackNews = [
  { id: "1", title: "Wavelength-Specific UV-C Inactivation of Viruses in Liquids: Dose-Response, Mechanistic Insights, and Structural Integrity", category: "Publication", summary: "Het\u00e9nyi R, Hanna D, et al. Viruses 2026, 18(3), 276. Systematic review and meta-analysis.", content: "https://doi.org/10.3390/v18030276", date: "2026-03-01" },
  { id: "2", title: "Sindbis Virus\u2013Host Interactions in Human Neuroblastoma Cells", category: "Publication", summary: "Bod\u00f3 K, ... Het\u00e9nyi R, Hanna D, et al. Viruses 2025, 17(10), 1346.", content: "https://doi.org/10.3390/v17101346", date: "2025-10-01" },
  { id: "3", title: "Evaluating the Protective Role of Intranasally Administered Avian-Derived IgY Against SARS-CoV-2", category: "Publication", summary: "Madai M, Hanna D, Het\u00e9nyi R, et al. Vaccines 2024, 12(12), 1422.", content: "https://doi.org/10.3390/vaccines12121422", date: "2024-12-01" },
  { id: "4", title: "RoLink Secures Partnership with National Laboratory of Virology", category: "Partnership", summary: "Strategic collaboration with University of P\u00e9cs BSL-4 facility.", content: "", date: "2025-09-15" },
  { id: "5", title: "EDF Integration Framework Announced", category: "Biodefense", summary: "RoLink technology integrated into European Defence Fund pandemic response protocols.", content: "", date: "2025-07-10" },
];

const categoryColors = {
  Partnership: "#EE1D25",
  Publication: "#10B981",
  Research: "#10B981",
  Biodefense: "#FFB000",
  Grant: "#8B5CF6",
  Media: "#EF4444",
};

export default function NewsFeed() {
  const [news, setNews] = useState(fallbackNews);

  useEffect(() => {
    axios.get(`${API}/news`).then(r => {
      if (r.data?.length) setNews(r.data);
    }).catch(() => {});
  }, []);

  return (
    <section id="news" data-testid="news-section" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="mb-16">
          <BilingualEyebrow en="News &amp; Publications" hu="H&iacute;rek &eacute;s publik&aacute;ci&oacute;k" className="text-[#4B5563]" />
          <BilingualHeading en="Latest Updates" hu="Legfrissebb h&iacute;rek" colorClass="text-[#111827]" />
        </motion.div>

        <div className="space-y-0 border-t border-[#E5E7EB]">
          {news.map((item, i) => {
            const isLink = item.content && item.content.startsWith("http");
            const Wrapper = isLink ? "a" : "div";
            const wrapperProps = isLink ? { href: item.content, target: "_blank", rel: "noopener noreferrer" } : {};
            return (
            <motion.article
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              data-testid={`news-card-${i}`}
            >
              <Wrapper {...wrapperProps} className="flex items-start gap-6 md:gap-10 py-8 border-b border-[#E5E7EB] group cursor-pointer hover:bg-[#F9FAFB] transition-colors px-4 -mx-4">
              <div className="hidden md:block shrink-0 w-28">
                <time className="font-mono text-sm text-[#9CA3AF]">{item.date}</time>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <Badge
                    variant="outline"
                    className="text-xs border rounded-none px-2 py-0.5"
                    style={{ borderColor: categoryColors[item.category] || "#E5E7EB", color: categoryColors[item.category] || "#4B5563" }}
                  >
                    {item.category}
                  </Badge>
                  <time className="md:hidden font-mono text-xs text-[#9CA3AF]">{item.date}</time>
                  {isLink && <span className="text-xs text-[#9CA3AF] font-mono hidden sm:inline">DOI</span>}
                </div>
                <h3 className="font-heading text-lg font-semibold text-[#111827] group-hover:text-[#EE1D25] transition-colors mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-[#4B5563] leading-relaxed">{item.summary}</p>
              </div>
              <ArrowUpRight size={20} className="text-[#9CA3AF] group-hover:text-[#EE1D25] transition-colors shrink-0 mt-1" />
              </Wrapper>
            </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
